import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown } from 'lucide-react';
import ChartHelpButton from '../ChartHelpButton';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import { getCleanOptions, getCleanPackageOptions, hidesPlaceholders, sizeReader, readSizeGroup, initCap } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import Colors from '../../../constants/Colors';
import {
  TERMINAL_PULSE_FIND_ALL,
  TERMINAL_PULSE_FIND_OPTIONS,
} from './terminalPricePulseConstants';
import {
  weekKey,
  formatRowDetails,
  applyArrayFilter,
  applyPackageFilter,
  multiSelectDropdownLabel,
} from './terminalPricePulseTableUtils';
import { applyCucumberSizeGroupFilter, applyCucumberSizeGranularityFilter } from './pulse/cucumbers/sizes.js';

const formatUsd = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
};

const formatDeltaUsd = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const formatDeltaPct = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const pct = n * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

const deltaTrendIcon = (delta) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return null;
  if (n > 0) return TrendingUp;
  if (n < 0) return TrendingDown;
  return Minus;
};

const DeltaCell = ({ value, formatter, theme }) => {
  const n = Number(value);
  const hasValue = value != null && value !== '' && Number.isFinite(n);
  if (!hasValue) {
    return <span style={{ color: theme.secondaryText }}>—</span>;
  }
  const Icon = deltaTrendIcon(n);
  const color = deltaColor(theme, n);
  return (
    <span className="inline-flex items-center justify-end gap-1">
      <Icon size={14} strokeWidth={2.25} style={{ color, flexShrink: 0 }} aria-hidden />
      <span style={{ color }}>{formatter(value)}</span>
    </span>
  );
};

const SORT_FIELDS = {
  week: 'week',
  origin: 'origin',
  terminal: 'terminal',
  price: 'price',
  weeklyUsd: 'weeklyUsd',
  weeklyPct: 'weeklyPct',
  fobUsd: 'fobUsd',
  fobPct: 'fobPct',
  details: 'details',
};

const rowSortValue = (row, field, commodity) => {
  switch (field) {
    case SORT_FIELDS.week:
      return (row.iso_year || 0) * 100 + (row.iso_week || 0);
    case SORT_FIELDS.origin:
      return (row.origin || '').toLowerCase();
    case SORT_FIELDS.terminal:
      return (row.location || '').toLowerCase();
    case SORT_FIELDS.price:
      return Number(row.price);
    case SORT_FIELDS.weeklyUsd:
      return Number(row.weekly_delta_usd);
    case SORT_FIELDS.weeklyPct:
      return Number(row.weekly_delta_pct);
    case SORT_FIELDS.fobUsd:
      return Number(row.fob_delta_usd);
    case SORT_FIELDS.fobPct:
      return Number(row.fob_delta_pct);
    case SORT_FIELDS.details:
      return formatRowDetails(row, commodity).toLowerCase();
    default:
      return '';
  }
};

const compareSortValues = (a, b) => {
  const aEmpty = a == null || a === '' || (typeof a === 'number' && !Number.isFinite(a));
  const bEmpty = b == null || b === '' || (typeof b === 'number' && !Number.isFinite(b));
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const deltaColor = (theme, delta) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return theme.secondaryText;
  if (n > 0) return '#22c55e';
  if (n < 0) return '#ef4444';
  return theme.secondaryText;
};

const makeToggle = (setter) => (item) => {
  setter((prev) => {
    const selected = Array.isArray(prev) ? prev : [];
    if (selected.includes(item)) return selected.filter((i) => i !== item);
    return [...selected, item];
  });
};

/**
 * Terminal Price Pulse Table (weekly, multi-week)
 */
const TerminalPricePulseTable = ({
  data = [],
  commodity = '',
  forceFiltersOpen = false,
  find = TERMINAL_PULSE_FIND_ALL,
  findLabel = '',
  onFindChange,
  selectedWeekKeys = [],
  onWeekKeysChange,
  weekOptions = [],
  isFetching = false,
}) => {
  const { t } = useTranslation();
  // Cucumbers only: Size Group sits above Package and Size reads size_extra_info (fallback size_granularity).
  const gatedCascade = hidesPlaceholders(commodity);
  const readSize = sizeReader(commodity);
  const sizeDisplay = gatedCascade ? initCap : undefined;
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [filtersExpanded, setFiltersExpanded] = useState(!!forceFiltersOpen);
  const initRef = useRef(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedType, setSelectedType] = useState('Conventional');
  const [selectedVarieties, setSelectedVarieties] = useState([]);
  const [selectedSizeGroups, setSelectedSizeGroups] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedStorages, setSelectedStorages] = useState([]);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState([]);
  const [selectedQualities, setSelectedQualities] = useState([]);
  const [selectedAppearances, setSelectedAppearances] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const [findModalOpen, setFindModalOpen] = useState(false);
  const [weekModalOpen, setWeekModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [sizeGroupModalOpen, setSizeGroupModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  useEffect(() => {
    initRef.current = false;
  }, [commodity, selectedWeekKeys.join('|')]);

  const weekFilteredData = useMemo(() => {
    if (!data?.length) return [];
    if (!selectedWeekKeys?.length) return data;
    const keySet = new Set(selectedWeekKeys);
    return data.filter((row) => keySet.has(weekKey({ iso_week: row.iso_week, iso_year: row.iso_year })));
  }, [data, selectedWeekKeys]);

  const availableOptions = useMemo(() => {
    const empty = {
      types: [],
      varieties: [],
      locations: [],
      origins: [],
      sizeGroups: [],
      sizes: [],
      grades: [],
      packages: [],
      storages: [],
      conditions: [],
      environments: [],
      qualities: [],
      appearances: [],
      properties: [],
    };
    if (!weekFilteredData.length) return empty;

    const origins = getCleanOptions(weekFilteredData, 'origin', commodity);
    let afterOrigins = weekFilteredData;
    if (selectedOrigins.length > 0 && selectedOrigins.length < origins.length) {
      afterOrigins = afterOrigins.filter((item) => item.origin && selectedOrigins.includes(item.origin));
    }
    const locations = getCleanOptions(afterOrigins, 'location', commodity);

    let filtered = weekFilteredData;
    if (selectedOrigins.length > 0 && selectedOrigins.length < origins.length) {
      filtered = filtered.filter((item) => item.origin && selectedOrigins.includes(item.origin));
    }
    if (selectedLocations.length > 0 && selectedLocations.length < locations.length) {
      filtered = filtered.filter((item) => item.location && selectedLocations.includes(item.location));
    }
    if (selectedType) {
      filtered = filtered.filter((item) => !item.type || item.type === selectedType);
    }

    const geoFiltered = filtered;
    const optPool = (narrowed) => {
      if (narrowed.length > 0) return narrowed;
      if (geoFiltered.length > 0) return geoFiltered;
      return weekFilteredData;
    };

    const types = getCleanOptions(optPool(filtered), 'type', commodity);
    const varieties = getCleanOptions(optPool(filtered), 'variety', commodity);
    const sizeGroups = gatedCascade ? getCleanOptions(optPool(filtered), readSizeGroup, commodity) : [];
    const sizes = getCleanOptions(optPool(filtered), readSize, commodity);
    const grades = getCleanOptions(optPool(filtered), 'grade', commodity);
    const packages = getCleanPackageOptions(optPool(filtered), commodity);
    const storages = getCleanOptions(optPool(filtered), 'storage', commodity);
    const conditions = getCleanOptions(optPool(filtered), 'condition', commodity);
    const environments = getCleanOptions(optPool(filtered), 'environment', commodity);
    const qualities = getCleanOptions(optPool(filtered), 'quality', commodity);
    const appearances = getCleanOptions(optPool(filtered), 'appearance', commodity);
    const properties = getCleanOptions(optPool(filtered), 'properties', commodity);

    return {
      types,
      varieties,
      locations,
      origins,
      sizeGroups,
      sizes,
      grades,
      packages,
      storages,
      conditions,
      environments,
      qualities,
      appearances,
      properties,
    };
  }, [weekFilteredData,
    selectedOrigins,
    selectedLocations,
    selectedType,
    selectedVarieties,
    selectedSizeGroups,
    selectedSizes,
    selectedGrades,
    selectedPackages,
    selectedStorages,
    selectedConditions,
    selectedEnvironments,
    selectedQualities,
    selectedAppearances,
    selectedProperties, commodity, gatedCascade, readSize]);

  const filteredData = useMemo(() => {
    let filtered = weekFilteredData;
    filtered = applyArrayFilter(filtered, 'origin', selectedOrigins, availableOptions.origins);
    filtered = applyArrayFilter(filtered, 'location', selectedLocations, availableOptions.locations);
    if (selectedType) {
      filtered = filtered.filter((item) => !item.type || item.type === selectedType);
    }
    filtered = applyArrayFilter(filtered, 'variety', selectedVarieties, availableOptions.varieties);
    if (gatedCascade) {
      filtered = applyCucumberSizeGroupFilter(filtered, selectedSizeGroups, availableOptions.sizeGroups, readSizeGroup);
      filtered = applyCucumberSizeGranularityFilter(filtered, selectedSizes, availableOptions.sizes, readSize);
    } else {
      filtered = applyArrayFilter(filtered, 'size', selectedSizes, availableOptions.sizes, readSize);
    }
    filtered = applyArrayFilter(filtered, 'grade', selectedGrades, availableOptions.grades);
    filtered = applyPackageFilter(filtered, selectedPackages, availableOptions.packages);
    filtered = applyArrayFilter(filtered, 'storage', selectedStorages, availableOptions.storages);
    filtered = applyArrayFilter(filtered, 'condition', selectedConditions, availableOptions.conditions);
    filtered = applyArrayFilter(filtered, 'environment', selectedEnvironments, availableOptions.environments);
    filtered = applyArrayFilter(filtered, 'quality', selectedQualities, availableOptions.qualities);
    filtered = applyArrayFilter(filtered, 'appearance', selectedAppearances, availableOptions.appearances);
    filtered = applyArrayFilter(filtered, 'properties', selectedProperties, availableOptions.properties);
    return filtered;
  }, [
    weekFilteredData,
    selectedOrigins,
    selectedLocations,
    selectedType,
    selectedVarieties,
    selectedSizeGroups,
    selectedSizes,
    selectedGrades,
    selectedPackages,
    selectedStorages,
    selectedConditions,
    selectedEnvironments,
    selectedQualities,
    selectedAppearances,
    selectedProperties,
    availableOptions,
    gatedCascade,
    readSize,
  ]);

  const setAllMulti = useCallback((setter, options) => {
    setter([...options]);
  }, []);

  useEffect(() => {
    if (!weekFilteredData.length || initRef.current) return;

    const origins = getCleanOptions(weekFilteredData, 'origin', commodity);
    const locations = getCleanOptions(weekFilteredData, 'location', commodity);
    const typeOptions = getCleanOptions(weekFilteredData, 'type', commodity);
    const defaultType = typeOptions.includes('Conventional') ? 'Conventional' : typeOptions[0] || '';

    let base = weekFilteredData.filter((item) => !item.type || item.type === defaultType);

    setSelectedOrigins(origins);
    setSelectedLocations(locations);
    setSelectedType(defaultType);
    setSelectedVarieties(getCleanOptions(base, 'variety', commodity));
    setSelectedSizeGroups(gatedCascade ? getCleanOptions(base, readSizeGroup, commodity) : []);
    setSelectedSizes(getCleanOptions(base, readSize, commodity));
    setSelectedGrades(getCleanOptions(base, 'grade', commodity));
    setSelectedPackages(
      getCleanPackageOptions(base, commodity)
    );
    setSelectedStorages(getCleanOptions(base, 'storage', commodity));
    setSelectedConditions(getCleanOptions(base, 'condition', commodity));
    setSelectedEnvironments(getCleanOptions(base, 'environment', commodity));
    setSelectedQualities(getCleanOptions(base, 'quality', commodity));
    setSelectedAppearances(getCleanOptions(base, 'appearance', commodity));
    setSelectedProperties(getCleanOptions(base, 'properties', commodity));

    initRef.current = true;
  }, [weekFilteredData, commodity, gatedCascade, readSize]);

  const handleOriginToggle = useMemo(() => makeToggle(setSelectedOrigins), []);
  const handleLocationToggle = useMemo(() => makeToggle(setSelectedLocations), []);
  const handleVarietyToggle = useMemo(() => makeToggle(setSelectedVarieties), []);
  const handleSizeGroupToggle = useMemo(() => makeToggle(setSelectedSizeGroups), []);
  const handleSizeToggle = useMemo(() => makeToggle(setSelectedSizes), []);
  const handleGradeToggle = useMemo(() => makeToggle(setSelectedGrades), []);
  const handlePackageToggle = useMemo(() => makeToggle(setSelectedPackages), []);
  const handleStorageToggle = useMemo(() => makeToggle(setSelectedStorages), []);
  const handleConditionToggle = useMemo(() => makeToggle(setSelectedConditions), []);
  const handleEnvironmentToggle = useMemo(() => makeToggle(setSelectedEnvironments), []);
  const handleQualityToggle = useMemo(() => makeToggle(setSelectedQualities), []);
  const handleAppearanceToggle = useMemo(() => makeToggle(setSelectedAppearances), []);
  const handlePropertiesToggle = useMemo(() => makeToggle(setSelectedProperties), []);

  const handleWeekToggle = useCallback(
    (key) => {
      const keys = Array.isArray(selectedWeekKeys) ? [...selectedWeekKeys] : [];
      const next = keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key];
      onWeekKeysChange?.(next);
    },
    [selectedWeekKeys, onWeekKeysChange]
  );

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }, [sortField]);

  const rows = useMemo(() => {
    const list = Array.isArray(filteredData) ? [...filteredData] : [];
    if (!sortField) return list;
    const dir = sortDir === 'desc' ? -1 : 1;
    return list.sort((a, b) => {
      const cmp = compareSortValues(rowSortValue(a, sortField, commodity), rowSortValue(b, sortField, commodity));
      return cmp * dir;
    });
  }, [filteredData, sortField, sortDir, commodity]);
  const showWeekColumn = (selectedWeekKeys?.length || 0) !== 1;

  const findDisplayLabel = useMemo(() => {
    const opt = TERMINAL_PULSE_FIND_OPTIONS.find((o) => o.value === find);
    if (opt) return t(opt.labelKey);
    return findLabel || t('filters.allPrices');
  }, [find, findLabel, t]);

  const weekDisplayLabel = useMemo(
    () =>
      multiSelectDropdownLabel(selectedWeekKeys, weekOptions.map((o) => o.value), {
        allLabel: t('charts.terminalPricePulseTable.allWeeks'),
        nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
        emptyLabel: t('charts.terminalPricePulseTable.selectWeek'),
        // Weeks are keyed as "2026-3"; a single week has to show its label.
        getOptionLabel: (value) => weekOptions.find((o) => o.value === value)?.label || value,
      }),
    [selectedWeekKeys, weekOptions, t]
  );

  const multiLabel = useCallback(
    (selected, options, allKey, getOptionLabel) =>
      multiSelectDropdownLabel(selected, options, {
        allLabel: t(allKey),
        nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
        emptyLabel: t('filters.select'),
        getOptionLabel,
      }),
    [t]
  );

  const packageFilter = availableOptions.packages.length > 0 && (
    <FilterDropdown
      label={t('filters.sku')}
      value={multiLabel(selectedPackages, availableOptions.packages, 'filters.allSkus')}
      onClick={() => setPackageModalOpen(true)}
      fontSize={fontSize}
    />
  );

  const helpText = t('charts.terminalPricePulseTable.help');
  const title = t('charts.terminalPricePulseTable.title');
  const colCount = showWeekColumn ? 9 : 8;

  if (!data.length && !weekOptions.length) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-transparent">
        <Header title={title} helpText={helpText} theme={theme} fontSize={fontSize} />
        <div className="flex flex-1 items-center justify-center px-4">
          <p className="text-center" style={{ fontSize, color: theme.secondaryText }}>
            {t('charts.terminalPricePulseTable.noDataWeekly')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[280px] min-w-0 flex-col overflow-hidden bg-transparent">
      <Header title={title} helpText={helpText} theme={theme} fontSize={fontSize} isFetching={isFetching} />

      <div className="shrink-0 px-4">
        {!forceFiltersOpen && (
          <button
            type="button"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex w-full items-center justify-between border-b py-2"
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>
              {t('filters.filters')}
            </span>
            {filtersExpanded ? (
              <ChevronUp size={16} style={{ color: theme.secondaryText }} />
            ) : (
              <ChevronDown size={16} style={{ color: theme.secondaryText }} />
            )}
          </button>
        )}

        {(forceFiltersOpen || filtersExpanded) && (
          <div className="grid shrink-0 grid-cols-2 gap-2 pt-2 md:grid-cols-3 lg:grid-cols-4">
            {weekOptions.length > 0 && (
              <FilterDropdown
                label={t('charts.terminalPricePulseTable.week')}
                value={weekDisplayLabel}
                onClick={() => setWeekModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            <FilterDropdown
              label={t('filters.findPrice')}
              value={findDisplayLabel}
              onClick={() => setFindModalOpen(true)}
              fontSize={fontSize}
            />
            {availableOptions.origins.length > 0 && (
              <FilterDropdown
                label={t('filters.origin')}
                value={multiLabel(selectedOrigins, availableOptions.origins, 'filters.allOrigins')}
                onClick={() => setOriginModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.locations.length > 0 && (
              <FilterDropdown
                label={t('filters.terminal')}
                value={multiLabel(selectedLocations, availableOptions.locations, 'filters.allTerminals')}
                onClick={() => setLocationModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.types.length > 0 && (
              <FilterDropdown
                label={t('filters.type')}
                value={selectedType || t('filters.selectType')}
                onClick={() => setTypeModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.varieties.length > 0 && (
              <FilterDropdown
                label={t('filters.variety')}
                value={multiLabel(selectedVarieties, availableOptions.varieties, 'filters.allVarieties')}
                onClick={() => setVarietyModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.sizeGroups.length > 0 && (
              <FilterDropdown
                label={t('filters.sizeGroup', 'Size Group')}
                value={multiLabel(selectedSizeGroups, availableOptions.sizeGroups, 'filters.allSizeGroups', sizeDisplay)}
                onClick={() => setSizeGroupModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {gatedCascade && packageFilter}
            {availableOptions.sizes.length > 0 && (
              <FilterDropdown
                label={gatedCascade ? t('filters.sizeGranularity', 'Size granularity') : t('filters.size')}
                value={multiLabel(selectedSizes, availableOptions.sizes, gatedCascade ? 'filters.allSizeGranularities' : 'filters.allSizes', sizeDisplay)}
                onClick={() => setSizeModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.grades.length > 0 && (
              <FilterDropdown
                label={t('filters.grade')}
                value={multiLabel(selectedGrades, availableOptions.grades, 'filters.allGrades')}
                onClick={() => setGradeModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {!gatedCascade && packageFilter}
            {availableOptions.storages.length > 0 && (
              <FilterDropdown
                label="Storage"
                value={multiLabel(selectedStorages, availableOptions.storages, 'filters.allStorage')}
                onClick={() => setStorageModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.conditions.length > 0 && (
              <FilterDropdown
                label={t('filters.condition')}
                value={multiLabel(selectedConditions, availableOptions.conditions, 'filters.allConditions')}
                onClick={() => setConditionModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.environments.length > 0 && (
              <FilterDropdown
                label="Environment"
                value={multiLabel(selectedEnvironments, availableOptions.environments, 'filters.allEnvironments')}
                onClick={() => setEnvironmentModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.qualities.length > 0 && (
              <FilterDropdown
                label="Quality"
                value={multiLabel(selectedQualities, availableOptions.qualities, 'filters.allQuality')}
                onClick={() => setQualityModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.appearances.length > 0 && (
              <FilterDropdown
                label="Appearance"
                value={multiLabel(selectedAppearances, availableOptions.appearances, 'filters.allAppearances')}
                onClick={() => setAppearanceModalOpen(true)}
                fontSize={fontSize}
              />
            )}
            {availableOptions.properties.length > 0 && (
              <FilterDropdown
                label="Properties"
                value={multiLabel(selectedProperties, availableOptions.properties, 'filters.allProperties')}
                onClick={() => setPropertiesModalOpen(true)}
                fontSize={fontSize}
              />
            )}
          </div>
        )}
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-auto px-4 pb-3">
        <table className="w-full min-w-[720px] text-xs" style={{ tableLayout: 'auto' }}>
          <thead className="sticky top-0 z-10" style={{ backgroundColor: theme.stickyHeaderBg }}>
            <tr>
              {showWeekColumn && (
              <SortableHeader
                label={t('charts.terminalPricePulseTable.week')}
                field={SORT_FIELDS.week}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="left"
                theme={theme}
                fontSize={fontSize}
              />
              )}
              <SortableHeader
                label={t('filters.origin')}
                field={SORT_FIELDS.origin}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="left"
                theme={theme}
                fontSize={fontSize}
              />
              <SortableHeader
                label={t('filters.terminal')}
                field={SORT_FIELDS.terminal}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="left"
                theme={theme}
                fontSize={fontSize}
              />
              <SortableHeader
                label={t('charts.terminalPricePulseTable.price')}
                field={SORT_FIELDS.price}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                theme={theme}
                fontSize={fontSize}
              />
              <SortableHeader
                label={t('charts.terminalPricePulseTable.weeklyUsd')}
                field={SORT_FIELDS.weeklyUsd}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                theme={theme}
                fontSize={fontSize}
                trendIcon
              />
              <SortableHeader
                label={t('charts.terminalPricePulseTable.weeklyPct')}
                field={SORT_FIELDS.weeklyPct}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                theme={theme}
                fontSize={fontSize}
                trendIcon
              />
              <SortableHeader
                label={t('charts.terminalPricePulseTable.fobUsd')}
                field={SORT_FIELDS.fobUsd}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                theme={theme}
                fontSize={fontSize}
                trendIcon
              />
              <SortableHeader
                label={t('charts.terminalPricePulseTable.fobPct')}
                field={SORT_FIELDS.fobPct}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                theme={theme}
                fontSize={fontSize}
                trendIcon
              />
              <SortableHeader
                label={t('charts.terminalPricePulseTable.details')}
                field={SORT_FIELDS.details}
                sortField={sortField}
                sortDir={sortDir}
                onSort={handleSort}
                align="left"
                theme={theme}
                fontSize={fontSize}
                className="min-w-[140px]"
              />
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((r, idx) => (
                <tr
                  key={`${weekKey(r)}-${r?.origin}-${r?.location}-${idx}`}
                  className="border-b hover:opacity-90"
                  style={{ borderColor: theme.tableRowBorder }}
                >
                  {showWeekColumn && (
                    <td className="whitespace-nowrap p-2" style={{ fontSize: fontSize - 1, color: theme.barLabelFill }}>
                      {r?.week_label || `W${r?.iso_week}`}
                    </td>
                  )}
                  <td className="p-2" style={{ fontSize: fontSize - 1, color: theme.barLabelFill }}>
                    {r?.origin || '—'}
                  </td>
                  <td className="p-2" style={{ fontSize: fontSize - 1, color: theme.barLabelFill }}>
                    {r?.location || '—'}
                  </td>
                  <td className="p-2 text-right" style={{ fontSize: fontSize - 1, color: theme.barLabelFill }}>
                    {formatUsd(r?.price)}
                  </td>
                  <td className="p-2 text-right" style={{ fontSize: fontSize - 1 }}>
                    <DeltaCell value={r?.weekly_delta_usd} formatter={formatDeltaUsd} theme={theme} />
                  </td>
                  <td className="p-2 text-right" style={{ fontSize: fontSize - 1 }}>
                    <DeltaCell value={r?.weekly_delta_pct} formatter={formatDeltaPct} theme={theme} />
                  </td>
                  <td className="p-2 text-right" style={{ fontSize: fontSize - 1 }}>
                    <DeltaCell value={r?.fob_delta_usd} formatter={formatDeltaUsd} theme={theme} />
                  </td>
                  <td className="p-2 text-right" style={{ fontSize: fontSize - 1 }}>
                    <DeltaCell value={r?.fob_delta_pct} formatter={formatDeltaPct} theme={theme} />
                  </td>
                  <td
                    className="max-w-[220px] whitespace-normal break-words p-2 align-top"
                    style={{ fontSize: fontSize - 1, color: theme.barLabelFill }}
                  >
                    {formatRowDetails(r, commodity)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={colCount} className="py-10 px-2 text-center" style={{ fontSize, color: theme.secondaryText }}>
                  {t('charts.noDataFilters')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <FilterModal
        isOpen={findModalOpen}
        onClose={() => setFindModalOpen(false)}
        title={t('filters.findPrice')}
        options={TERMINAL_PULSE_FIND_OPTIONS.map((o) => o.value)}
        selectedValues={find}
        onSelect={(value) => {
          onFindChange?.(value);
          setFindModalOpen(false);
        }}
        displayFormatter={(value) => {
          const opt = TERMINAL_PULSE_FIND_OPTIONS.find((o) => o.value === value);
          return opt ? t(opt.labelKey) : value;
        }}
      />

      <FilterModal
        isOpen={weekModalOpen}
        onClose={() => setWeekModalOpen(false)}
        title={t('charts.terminalPricePulseTable.selectWeeks')}
        options={weekOptions.map((o) => o.value)}
        selectedValues={selectedWeekKeys}
        onSelect={handleWeekToggle}
        multiSelect
        onSelectAll={() => onWeekKeysChange?.(weekOptions.map((o) => o.value))}
        onClearAll={() => onWeekKeysChange?.([])}
        displayFormatter={(value) => weekOptions.find((o) => o.value === value)?.label || value}
      />

      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title={t('filters.selectOrigins')}
        options={availableOptions.origins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedOrigins, availableOptions.origins)}
        onClearAll={() => setSelectedOrigins([])}
      />

      <FilterModal
        isOpen={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        title={t('filters.selectTerminal')}
        options={availableOptions.locations}
        selectedValues={selectedLocations}
        onSelect={handleLocationToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedLocations, availableOptions.locations)}
        onClearAll={() => setSelectedLocations([])}
      />

      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('filters.selectType')}
        options={availableOptions.types.length > 0 ? availableOptions.types : ['Conventional', 'Organic']}
        selectedValues={selectedType}
        onSelect={(value) => setSelectedType(value)}
      />

      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title={t('filters.selectVariety')}
        options={availableOptions.varieties}
        selectedValues={selectedVarieties}
        onSelect={handleVarietyToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedVarieties, availableOptions.varieties)}
        onClearAll={() => setSelectedVarieties([])}
      />

      <FilterModal
        isOpen={sizeGroupModalOpen}
        onClose={() => setSizeGroupModalOpen(false)}
        title={t('filters.selectSizeGroup', 'Select Size Group')}
        options={availableOptions.sizeGroups}
        selectedValues={selectedSizeGroups}
        onSelect={handleSizeGroupToggle}
        displayFormatter={sizeDisplay}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedSizeGroups, availableOptions.sizeGroups)}
        onClearAll={() => setSelectedSizeGroups([])}
      />

      <FilterModal
        isOpen={sizeModalOpen}
        onClose={() => setSizeModalOpen(false)}
        title={t('filters.selectSize')}
        options={availableOptions.sizes}
        selectedValues={selectedSizes}
        onSelect={handleSizeToggle}
        displayFormatter={sizeDisplay}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedSizes, availableOptions.sizes)}
        onClearAll={() => setSelectedSizes([])}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title={t('filters.selectGrade')}
        options={availableOptions.grades}
        selectedValues={selectedGrades}
        onSelect={handleGradeToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedGrades, availableOptions.grades)}
        onClearAll={() => setSelectedGrades([])}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title={t('filters.selectSku')}
        options={availableOptions.packages}
        selectedValues={selectedPackages}
        onSelect={handlePackageToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedPackages, availableOptions.packages)}
        onClearAll={() => setSelectedPackages([])}
      />

      <FilterModal
        isOpen={storageModalOpen}
        onClose={() => setStorageModalOpen(false)}
        title="Select Storage"
        options={availableOptions.storages}
        selectedValues={selectedStorages}
        onSelect={handleStorageToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedStorages, availableOptions.storages)}
        onClearAll={() => setSelectedStorages([])}
      />

      <FilterModal
        isOpen={conditionModalOpen}
        onClose={() => setConditionModalOpen(false)}
        title={t('filters.selectCondition')}
        options={availableOptions.conditions}
        selectedValues={selectedConditions}
        onSelect={handleConditionToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedConditions, availableOptions.conditions)}
        onClearAll={() => setSelectedConditions([])}
      />

      <FilterModal
        isOpen={environmentModalOpen}
        onClose={() => setEnvironmentModalOpen(false)}
        title="Select Environment"
        options={availableOptions.environments}
        selectedValues={selectedEnvironments}
        onSelect={handleEnvironmentToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedEnvironments, availableOptions.environments)}
        onClearAll={() => setSelectedEnvironments([])}
      />

      <FilterModal
        isOpen={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        title="Select Quality"
        options={availableOptions.qualities}
        selectedValues={selectedQualities}
        onSelect={handleQualityToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedQualities, availableOptions.qualities)}
        onClearAll={() => setSelectedQualities([])}
      />

      <FilterModal
        isOpen={appearanceModalOpen}
        onClose={() => setAppearanceModalOpen(false)}
        title="Select Appearance"
        options={availableOptions.appearances}
        selectedValues={selectedAppearances}
        onSelect={handleAppearanceToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedAppearances, availableOptions.appearances)}
        onClearAll={() => setSelectedAppearances([])}
      />

      <FilterModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        title="Select Properties"
        options={availableOptions.properties}
        selectedValues={selectedProperties}
        onSelect={handlePropertiesToggle}
        multiSelect
        onSelectAll={() => setAllMulti(setSelectedProperties, availableOptions.properties)}
        onClearAll={() => setSelectedProperties([])}
      />
    </div>
  );
};

function SortableHeader({
  label,
  field,
  sortField,
  sortDir,
  onSort,
  align = 'left',
  theme,
  fontSize,
  trendIcon = false,
  className = '',
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const active = sortField === field;
  const highlighted = hovered || active;
  const accent = Colors.primary;
  const textColor = highlighted ? accent : theme.secondaryText;
  const SortIcon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : null;
  const alignClass = align === 'right' ? 'justify-end text-right' : 'justify-start text-left';
  const tooltip = t('charts.terminalPricePulseTable.clickToSort');

  return (
    <th className={`p-2 font-medium ${className}`} style={{ fontSize: fontSize - 2 }}>
      <button
        type="button"
        onClick={() => onSort(field)}
        title={tooltip}
        aria-label={`${label}. ${tooltip}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`inline-flex w-full cursor-pointer items-center gap-1 transition-colors duration-150 ${alignClass}`}
        style={{ color: textColor }}
      >
        {trendIcon ? (
          <TrendingUp
            size={14}
            strokeWidth={2.25}
            className="shrink-0"
            style={{ color: textColor }}
            aria-hidden
          />
        ) : null}
        <span>{label}</span>
        {SortIcon ? (
          <SortIcon size={12} className="shrink-0" style={{ color: textColor }} aria-hidden />
        ) : null}
      </button>
    </th>
  );
}

function Header({ title, helpText, theme, fontSize, isFetching }) {
  return (
    <div className="shrink-0 border-b px-4 py-2" style={{ borderColor: theme.borderColor }}>
      <div className="flex items-center gap-2">
        <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
          {title}
        </h3>
        <ChartHelpButton title={title} description={helpText} />
        {isFetching ? (
          <span className="ml-2 text-xs font-normal normal-case" style={{ color: theme.secondaryText }}>
            …
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default TerminalPricePulseTable;
